import React from 'react'
import { WallPropertiesProps } from './types'
import {
  PropertySection,
  SectionHeader,
  SectionTitle,
  CollapseIcon,
  SectionContent,
  PropertyGroup,
  Label,
  Input,
  InfoText,
  Select,
  Checkbox,
} from '../styled'
import {
  pixelsToReal,
  realToPixels,
  getUnitSingular,
  formatStandardWallThickness,
  getDefaultCeilingHeight,
} from '../helpers'

const WallProperties: React.FC<WallPropertiesProps> = ({
  selectedElement,
  mapSettings,
  localProperties,
  isCollapsed,
  onToggleSection,
  updateProperty,
  updateMeasurement,
  onUpdateElement,
}) => {
  return (
    <PropertySection>
      <SectionHeader isCollapsed={isCollapsed} onClick={onToggleSection}>
        <SectionTitle>Wall Properties</SectionTitle>
        <CollapseIcon isCollapsed={isCollapsed}>▼</CollapseIcon>
      </SectionHeader>
      <SectionContent isCollapsed={isCollapsed}>
        <PropertyGroup>
          <Label>Wall Thickness ({getUnitSingular(mapSettings)})</Label>
          <Input
            type="number"
            step="0.1"
            value={(
              selectedElement.measurements?.realWidth ||
              pixelsToReal(localProperties.thickness || 6, mapSettings)
            ).toFixed(1)}
            onChange={e => {
              const realThickness = Number(e.target.value)
              updateMeasurement('realWidth', realThickness)
              updateProperty(
                'thickness',
                realToPixels(realThickness, mapSettings),
              )
            }}
          />
          <InfoText>
            Standard: {formatStandardWallThickness(mapSettings)}
          </InfoText>
        </PropertyGroup>
        <PropertyGroup>
          <Label>Wall Height ({getUnitSingular(mapSettings)})</Label>
          <Input
            type="number"
            step="0.1"
            value={(
              selectedElement.measurements?.realHeight ||
              getDefaultCeilingHeight(mapSettings)
            ).toFixed(1)}
            onChange={e =>
              updateMeasurement('realHeight', Number(e.target.value))
            }
          />
          <InfoText>Standard ceiling height</InfoText>
        </PropertyGroup>
        <PropertyGroup>
          <Label>Material</Label>
          <Select
            value={localProperties.material || 'drywall'}
            onChange={e => updateProperty('material', e.target.value)}>
            <option value="drywall">Drywall</option>
            <option value="brick">Brick</option>
            <option value="concrete">Concrete</option>
            <option value="wood">Wood Frame</option>
            <option value="glass">Glass</option>
            <option value="metal">Metal</option>
          </Select>
        </PropertyGroup>
        <PropertyGroup>
          <Label>End Cap Style</Label>
          <Select
            value={localProperties.endCapType || 'square'}
            onChange={e => updateProperty('endCapType', e.target.value)}>
            <option value="butt">Flat (Square)</option>
            <option value="round">Round</option>
            <option value="square">Square (Extended)</option>
          </Select>
          <InfoText>How wall ends connect at corners</InfoText>
        </PropertyGroup>
        <PropertyGroup>
          <Label>
            <Checkbox
              checked={localProperties.loadBearing || false}
              onChange={e => updateProperty('loadBearing', e.target.checked)}
            />
            Load Bearing Wall
          </Label>
        </PropertyGroup>
        <PropertyGroup>
          <Label>Wall Label</Label>
          <Input
            value={localProperties.label || ''}
            onChange={e => updateProperty('label', e.target.value)}
            placeholder="e.g., Exterior Wall, Load Bearing"
          />
        </PropertyGroup>
        <PropertyGroup>
          <Label>
            <Checkbox
              checked={selectedElement.locked || false}
              onChange={e =>
                onUpdateElement(selectedElement.id, {
                  locked: e.target.checked,
                })
              }
            />
            Lock from Dragging
          </Label>
        </PropertyGroup>
      </SectionContent>
    </PropertySection>
  )
}

export { WallProperties }
